import {Component, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {DoodOnSiteLocationModel} from "@core/models/on-site-location.model";
import {OnSiteLocationsService} from '@core/services/on-site-locations/on-site-locations.service';
import {OnSiteLocationStoreDispatcher} from '@common/dispatchers/on-site-location.dispatcher';
import {DoodOnSiteLocationSourceValues} from '@store/on-site-location/on-site-location.enum';
import {RouterHelperService} from "@core/services/router-helper/router-helper.service";
import {NavigationHistoryService} from "@core/services/navigation-history/navigation-history.service";

@Component({
  selector: 'app-pat-table-selector-page',
  template: `
    <div class="fixed inset-0 flex flex-col pt-2 overflow-hidden">
      <ng-container *ngIf="isLoading">
        <div class="flex items-center justify-center flex-1">
          <img
            class="h-10 w-10 animate-spin"
            src="assets/images/spinner.svg"
            alt="Loading"
          />
        </div>
      </ng-container>

      <ng-container *ngIf="!isLoading && !shopId">
        <div class="flex items-center justify-center flex-1 px-5 text-center text-xl font-bold text-neutral-700">
          Shop inconnu
        </div>
      </ng-container>

      <ng-container *ngIf="!isLoading && shopId">
        <!-- Affichage liste -->
        <div class="flex-1 overflow-y-auto px-5 pb-3 flex flex-col">
          <ng-container *ngIf="currentTab === 'keyboard'">
            <div class="space-y-3 mt-auto">
              <div
                *ngFor="let table of filteredTables"
                (click)="selectTable(table.id)"
                class="rounded bg-neutral-100 px-4 py-3 shadow-sm active:bg-neutral-300 cursor-pointer transition-colors"
              >
                <div class="font-bold text-neutral-900">{{ table.name }}</div>
              </div>
            </div>
          </ng-container>

          <ng-container *ngIf="currentTab === 'all'">
            <div class="space-y-3 pt-2">
              <div
                *ngFor="let table of sortedOnSiteLocations"
                (click)="selectTable(table.id)"
                class="rounded bg-neutral-100 px-4 py-3 shadow-sm active:bg-neutral-300 cursor-pointer transition-colors"
              >
                <div class="font-bold text-neutral-900">{{ table.name }}</div>
              </div>
            </div>
          </ng-container>
        </div>

        <!-- Bloc fixe clavier + onglets -->
        <div class="sticky bottom-0 w-full bg-white">
          <ng-container *ngIf="currentTab === 'keyboard'">
            <div class="shadow-inner px-5">
              <div class="py-3 text-center text-xl font-bold text-neutral-700">
                {{ input || '–' }}
              </div>
              <div class="grid grid-cols-3 gap-2 pb-4">
                <button *ngFor="let key of keypadLayout"
                        (click)="handleKeyPress(key)"
                        [ngClass]="{
                          'bg-yellow-200 text-yellow-900': key === '<',
                          'bg-red-200 text-red-800 text-xs': key === 'EFFACER',
                          'bg-neutral-200 text-neutral-800 text-xl font-bold': key !== '<' && key !== 'EFFACER'
                        }"
                        class="h-16 rounded active:scale-95 active:bg-neutral-300 transition-all duration-100">
                  {{ key }}
                </button>
              </div>
            </div>
          </ng-container>

          <div class="shadow-inner px-5 py-4">
            <div class="flex space-x-3">
              <button
                class="flex-1 rounded-full border border-neutral-300 bg-neutral-100 py-2 text-center text-sm font-bold text-neutral-700"
                [class.bg-primary-600]="currentTab === 'keyboard'"
                [class.text-white]="currentTab === 'keyboard'"
                (click)="setTab('keyboard')"
              >
                {{ 'pay-at-table.select-table.tab-keyboard' | translate }}
              </button>
              <button
                class="flex-1 rounded-full border border-neutral-300 bg-neutral-100 py-2 text-center text-sm font-bold text-neutral-700"
                [class.bg-primary-600]="currentTab === 'all'"
                [class.text-white]="currentTab === 'all'"
                (click)="setTab('all')"
              >
                {{ 'pay-at-table.select-table.tab-all' | translate }}
              </button>
            </div>
          </div>
        </div>
      </ng-container>
    </div>
  `,
  styles: []
})

export class PatTableSelectorPageComponent implements OnInit {
  onSiteLocations: DoodOnSiteLocationModel[] = [];
  input = '';
  keypadLayout: string[] = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '<', '0', 'EFFACER'];
  isLoading = true;
  currentTab: 'keyboard' | 'all' = 'keyboard';
  shopId: string | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private onSiteLocationsService: OnSiteLocationsService,
    private onSiteLocationDispatcher: OnSiteLocationStoreDispatcher,
    private routerHelper: RouterHelperService,
    private navigationHistoryService: NavigationHistoryService,
    private activatedRoute: ActivatedRoute,
  ) {
    this.navigationHistoryService.pushStateFromActivatedRouteSnapshot(
      'home',
      this.activatedRoute.snapshot,
    );
  }

  ngOnInit(): void {
    this.shopId = this.route.snapshot.queryParamMap.get('shop') || localStorage.getItem('shopId');

    if (!this.shopId) {
      this.isLoading = false;
      return;
    }

    localStorage.setItem('shopId', this.shopId);

    // Clé de cache localStorage spécifique au shop
    const cacheKey = `onsiteLocations_${this.shopId}`;
    const cached = localStorage.getItem(cacheKey);

    if (cached) {
      try {
        const parsed: DoodOnSiteLocationModel[] = JSON.parse(cached);
        this.onSiteLocations = parsed
          .filter(loc => !!loc.name)
          .sort((a, b) => a.name.localeCompare(b.name));
        this.isLoading = false; // afficher les données tout de suite
      } catch (e) {
        console.warn('Erreur lors du parsing du cache onsiteLocations', e);
      }
    }

    // Mise à jour des données depuis l’API dans tous les cas
    this.onSiteLocationsService.loadOnSiteLocations$(this.shopId).subscribe(locations => {
      const validLocations = locations
        .map(location => ({ ...location }))
        .filter(loc => !!loc.name)
        .sort((a, b) => a.name.localeCompare(b.name));

      this.onSiteLocations = validLocations;

      try {
        localStorage.setItem(cacheKey, JSON.stringify(validLocations));
      } catch (e) {
        console.warn('Erreur lors de l’enregistrement du cache onsiteLocations', e);
      }

      this.isLoading = false;
    });
  }


  get filteredTables(): DoodOnSiteLocationModel[] {
    return this.onSiteLocations.filter(loc => loc.name.includes(this.input));
  }

  get sortedOnSiteLocations(): DoodOnSiteLocationModel[] {
    return [...this.onSiteLocations];
  }

  handleKeyPress(key: string): void {
    if (key === 'EFFACER') {
      this.clearInput();
    } else if (key === '<') {
      this.input = this.input.slice(0, -1);
    } else {
      this.input += key;
    }
  }

  clearInput(): void {
    this.input = '';
  }

  setTab(tab: 'keyboard' | 'all'): void {
    this.currentTab = tab;
    if (tab === 'all') {
      this.clearInput();
    }
  }

  selectTable(id: string): void {
    this.onSiteLocationDispatcher.updateSettings({
      id,
      lastLocationId: id,
      source: DoodOnSiteLocationSourceValues.fromUser
    });
    this.onSiteLocationDispatcher.updateActive(id);
    this.router.navigate([this.routerHelper.translateRoute('/pay-at-table/overview')]);
  }
}
